import { useState } from "react";

import { Eye, EyeOff } from "lucide-react";

import {
  AtlasLabelField,
  type AtlasLabelFieldProps,
} from "@/components/form/AtlasLabelField";
import { useFormField } from "@/components/ui/form";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface AtlasThemePasswordFieldProps
  extends Omit<AtlasLabelFieldProps, "type" | "endAdornment"> {
  onVisibilityToggle?: (isVisible: boolean) => void;
  buttonClassName?: string;
  showPasswordText?: string;
  hidePasswordText?: string;
  wrapperClassName?: string;
}

export const AtlasThemePasswordField = ({
  onVisibilityToggle,
  buttonClassName,
  showPasswordText = "Show password",
  hidePasswordText = "Hide password",
  wrapperClassName,
  className,
  variant = "default",
  size = "default",
  error,
  ...props
}: AtlasThemePasswordFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);

  // Get the form field context for proper ID association
  const { formItemId } = useFormField();

  const handleToggle = () => {
    const newState = !showPassword;
    setShowPassword(newState);
    onVisibilityToggle?.(newState);
  };

  // Determine variant based on error prop
  const effectiveVariant = error ? "error" : variant;

  const passwordButton = (
    <Tooltip>
      <TooltipTrigger
        type="button"
        onClick={handleToggle}
        className={cn(
          // Layout & Positioning
          "cursor-pointer h-full w-full min-w-[44px] mr-[-5px]",

          // Border Radius - matches input field
          "rounded-r-[14px] rounded-l-none",

          // Colors
          "text-white/70",
          "hover:text-white",

          // Transitions
          "transition-colors",

          // Focus States
          "focus:bg-white/10 focus-visible:ring-0 focus-visible:ring-offset-0",

          // Layout
          "flex items-center justify-center",

          // Button-like styles
          "bg-transparent border-none outline-none",

          buttonClassName
        )}
        aria-label={showPassword ? hidePasswordText : showPasswordText}
      >
        {showPassword ? <EyeOff /> : <Eye />}
      </TooltipTrigger>
      <TooltipContent
        sideOffset={0}
        className="bg-black text-white -mb-2 ml-0.5"
      >
        {showPassword ? hidePasswordText : showPasswordText}
      </TooltipContent>
    </Tooltip>
  );

  return (
    <div className={cn("w-full", wrapperClassName)}>
      <AtlasLabelField
        id={formItemId}
        className={className}
        variant={effectiveVariant}
        size={size}
        error={error}
        type={showPassword ? "text" : "password"}
        endAdornment={passwordButton}
        {...props}
      />
    </div>
  );
};

AtlasThemePasswordField.displayName = "AtlasThemePasswordField";
